import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone } from "lucide-react";
import { cn } from "@/lib/utils";
import logo from "@/assets/logo.webp";
import logoDark from "@/assets/logo-dark.webp";

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  // Pages with dark hero backgrounds where white text is needed
  const hasDarkHero = location.pathname === "/" || location.pathname === "/discover";

  // Use dark logo when scrolled OR on pages without dark hero
  const currentLogo = isScrolled || !hasDarkHero ? logoDark : logo;

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "Suites", path: "/booking" },
    { name: "Discover", path: "/discover" },
    { name: "About", path: "/about" },
    { name: "Contact", path: "/contact" },
  ];

  return (
    <nav
      className={cn(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled
          ? "bg-white/95 backdrop-blur-md shadow-card"
          : hasDarkHero
          ? "bg-gradient-to-b from-black/30 to-transparent"
          : "bg-transparent"
      )}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo with fade transition */}
          <Link to="/" className="flex items-center group relative h-12 w-40 shrink-0" aria-label="Harbor Heights Extended Stay - Home">
            <img 
              src={logo} 
              alt=""
              aria-hidden="true"
              width={160}
              height={48}
              className={cn(
                "h-12 w-auto transition-opacity duration-500 group-hover:scale-105 absolute inset-0 object-contain object-left",
                isScrolled || !hasDarkHero ? "opacity-0" : "opacity-100"
              )}
            />
            <img 
              src={logoDark} 
              alt=""
              aria-hidden="true"
              width={160}
              height={48}
              className={cn(
                "h-12 w-auto transition-opacity duration-500 group-hover:scale-105 absolute inset-0 object-contain object-left",
                isScrolled || !hasDarkHero ? "opacity-100" : "opacity-0"
              )}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={cn(
                  "px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300",
                  location.pathname === link.path
                    ? "text-harbor-blue bg-accent"
                    : isScrolled
                    ? "text-foreground hover:text-harbor-blue hover:bg-accent/50"
                    : hasDarkHero
                    ? "text-white hover:bg-white/10"
                    : "text-harbor-blue hover:bg-accent/50"
                )}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Desktop CTA Buttons */}
          <div className="hidden lg:flex items-center space-x-3">
            <a 
              href="tel:+1-712-224-7003" 
              className={cn(
                "flex items-center space-x-2 text-sm font-medium transition-colors",
                isScrolled
                  ? "text-muted-foreground hover:text-harbor-blue"
                  : hasDarkHero
                  ? "text-white hover:text-warm-gold"
                  : "text-muted-foreground hover:text-harbor-blue"
              )}
            >
              <Phone className="w-4 h-4" />
              <span className="hidden xl:inline">(712) 224-7003</span>
            </a>
            <Button asChild variant="hero" size="lg">
              <a href="tel:+1-712-224-7003">Call to Book</a>
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-accent transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? (
              <X className={cn("w-6 h-6", isScrolled || !hasDarkHero ? "text-foreground" : "text-white")} />
            ) : (
              <Menu className={cn("w-6 h-6", isScrolled || !hasDarkHero ? "text-foreground" : "text-white")} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-border shadow-lg animate-fade-in">
          <div className="container mx-auto px-4 py-6 space-y-4">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setIsMobileMenuOpen(false)}
                className={cn(
                  "block px-4 py-3 rounded-lg text-base font-medium transition-colors",
                  location.pathname === link.path
                    ? "bg-accent text-harbor-blue"
                    : "text-foreground hover:bg-accent/50"
                )}
              >
                {link.name}
              </Link>
            ))}
            <div className="pt-4 space-y-3">
              <a
                href="tel:+1-712-224-7003"
                className="flex items-center justify-center space-x-2 px-4 py-3 text-harbor-blue font-medium"
              >
                <Phone className="w-5 h-5" />
                <span>(712) 224-7003</span>
              </a>
              <Button asChild variant="hero" size="lg" className="w-full">
                <a href="tel:+1-712-224-7003" onClick={() => setIsMobileMenuOpen(false)}>
                  Call to Book
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
